(ns minorodata.sandbox.subs
  (:require
   ["date-fns" :refer [parseISO]]
   [clojure.string :as str]
   [minorodata.app.utils :refer [drop-ns-in-keys get-dag-id try-parse-features]]
   [minorodata.pipeline-dag.layout :refer [container?]]
   [minorodata.utils :refer [find-by-field find-first]]
   [re-frame.core :as re-frame]))


(defn pipeline-editor-local-storage-key [uuid]
  (str "pipeline-editor-" uuid))

(re-frame/reg-sub
 :pipeline-editor.subs/ready?
 (fn [db]
   (not-empty (get db :pipeline-editor))))

(re-frame/reg-sub
 :pipeline-editor.subs/tabs
 (fn [db]
   (get-in db [:pipeline-editor :tabs])))

(re-frame/reg-sub
 :pipeline-editor.subs/active-tab-id
 (fn [db]
   (get-in db [:pipeline-editor :active-tab-id])))

(re-frame/reg-sub
 :pipeline-editor.subs/active-tab
 :<- [:pipeline-editor.subs/active-tab-id]
 :<- [:pipeline-editor.subs/tabs]
 (fn [[active-tab-id tabs]]
   (find-by-field tabs :id active-tab-id)))

(re-frame/reg-sub
 :pipeline-editor.subs/preview-target
 :<- [:app-route/prop :app-route/query]
 :<- [:pipeline-editor.subs/active-tab]
 (fn [[query active-tab]]
   (cond
     (:tab-preview-type query)
     {:type     (:tab-preview-type query)
      :id       (:tab-preview-id query)
      :group-id (:tab-preview-group-id query)
      :preview  true}

     active-tab
     {:type     (:type active-tab)
      :id       (:id active-tab)
      :group-id (:group-id active-tab)})))

;; See :pipeline-editor.events/request-details-tab — nil once the details pane has
;; consumed it.
(re-frame/reg-sub
 :pipeline-editor.subs/details-tab-request
 (fn [db]
   (get-in db [:pipeline-editor :details-tab-request])))

(re-frame/reg-sub
 :pipeline-editor.subs/panel
 (fn [db]
   (get-in db [:pipeline-editor :panel])))

(re-frame/reg-sub
 :pipeline-editor.subs/horizontal-sizes
 :<- [:pipeline-editor.subs/panel]
 (fn [panel]
   (get panel :horizontal-sizes [280 99999 480])))

(re-frame/reg-sub
 :pipeline-editor.subs/left-sidebar-collapsed?
 :<- [:pipeline-editor.subs/panel]
 (fn [panel]
   (boolean (:left-sidebar-collapsed? panel))))

(re-frame/reg-sub
 :pipeline-editor.subs/right-sidebar-collapsed?
 :<- [:pipeline-editor.subs/panel]
 (fn [panel]
   (boolean (:right-sidebar-collapsed? panel))))

(re-frame/reg-sub
 :pipeline-editor.subs/picker-column-collapsed?
 :<- [:pipeline-editor.subs/panel]
 (fn [panel [_ column]]
   (boolean (get-in panel [:collapsed-columns (keyword column)]))))

(re-frame/reg-sub
 :pipeline-editor.subs/table
 (fn [_ [_ id]]
   @(re-frame/subscribe [:pipeline-table/pull [:pipeline-table/id id]])))

(re-frame/reg-sub
 :pipeline-editor.subs/raw-data
 (fn [db _]
   (:raw-data db)))

(re-frame/reg-sub
 :pipeline-editor.subs/extract
 (fn [[_ id]]
   [(re-frame/subscribe [:connections.subs/extract-by-master-id id])
    (re-frame/subscribe [:pipeline-editor.subs/sources])])
 (fn [[raw sources] _]
   (when raw
     (let [extract (drop-ns-in-keys raw)]
       (assoc extract
              :source (find-by-field sources :source-id (:source-id extract)))))))

(re-frame/reg-sub
 :pipeline-editor.subs/sources
 :<- [:source/pull-many]
 (fn [sources]
   (->> sources
        (sort-by (fn [{:source/keys [order name]}]
                   [(or order 0) (some-> name str/lower-case)]))
        (mapv drop-ns-in-keys))))

(re-frame/reg-sub
 :pipeline-editor.subs/source-extracts
 (fn [[_ source-id]]
   (re-frame/subscribe [:connections.subs/extracts-for-source source-id]))
 (fn [{:keys [extracts]}]
   (->> extracts
        (sort-by (fn [{:extract/keys [order name]}]
                   [(or order 0) (some-> name str/lower-case)]))
        (mapv drop-ns-in-keys))))

(re-frame/reg-sub
 :pipeline-editor.subs/table-preview
 (fn [[_ table-id]]
   (re-frame/subscribe [:pipeline-table-preview/pull [:pipeline-table-preview/id table-id]]))
 (fn [preview]
   (let [{:pipeline-table-preview/keys [rows headers error]} preview]
     (when preview
       {:error   error
        :rows    rows
        :headers headers}))))

(re-frame/reg-sub
 :pipeline-editor.subs/transform-groups
 :<- [:pipeline-group/pull-many]
 (fn [groups]
   (sort-by :pipeline-group/order groups)))

(re-frame/reg-sub
 :pipeline-editor.subs/group-transforms
 (fn [[_ group-id]]
   [(re-frame/subscribe [:sandbox.subs/enabled?])
    (re-frame/subscribe [:pipeline-group/pull [:pipeline-group/id group-id]
                         '[* {:pipeline-group/transforms [*]}]])
    (re-frame/subscribe [:sandbox-transform/pull-many])])
 (fn [[sandbox? {:keys [pipeline-group/transforms]} sandbox-transforms] [_ group-id]]
   (mapv
    drop-ns-in-keys
    (if sandbox?
      (->> sandbox-transforms
           (filter (fn [itm] (= group-id (:sandbox-transform/group-id itm))))
           (sort-by #(str/lower-case (:sandbox-transform/name %))))
      (sort-by :pipeline-transform/order transforms)))))

(re-frame/reg-sub
 :pipeline-editor.subs/transform-search-results
 :<- [:pipeline-transform-search/pull '[{:pipeline-transform-search/transforms [*]}]]
 :<- [:pipeline-group/pull-many]
 (fn [[{:keys [pipeline-transform-search/transforms]} groups]]
   (let [transforms (mapv (comp
                           #(assoc % :group (drop-ns-in-keys (find-by-field groups :pipeline-group/id (:group-id %))))
                           drop-ns-in-keys)
                          transforms)]
     transforms)))

(re-frame/reg-sub
 :pipeline-editor.subs/transform-state
 (fn []
   [(re-frame/subscribe [:sandbox.subs/enabled?])
    (re-frame/subscribe [:sandbox-updates/pull])])
 (fn [[sandbox? updates] [_ master-id]]
   (when sandbox?
     (let [type (some-> (find-first #(= master-id (:master-id %)) (:sandbox-updates/updates updates))
                        :type)]
       (case type
         "UPDATE" :updated
         "CREATE" :created
         nil)))))


(re-frame/reg-sub
 :pipeline-editor.subs/full-transform
 (fn []
   [(re-frame/subscribe [:sandbox.subs/enabled?])
    (re-frame/subscribe [:pipeline-transform/pull-many])
    (re-frame/subscribe [:sandbox-transform/pull-many])
    (re-frame/subscribe [:pipeline-group/pull-many])])
 (fn [[sandbox? transforms sandbox-transforms groups] [_ master-id]]
   (let [transforms (mapv drop-ns-in-keys
                          (if sandbox? sandbox-transforms transforms))
         transform  (find-first (fn [t] (and (= (:master-id t) master-id)
                                             (not (:stale t))))
                                transforms)
         group      (find-by-field groups :pipeline-group/id (:group-id transform))]
     (some-> transform (assoc :group (drop-ns-in-keys group))))))

(re-frame/reg-sub
 :pipeline-editor.subs/right-sidebar-view
 (fn [db _]
   (or (get-in db [:pipeline-editor :right-sidebar-view])
       :summary)))

(re-frame/reg-sub
 :pipeline-editor.subs/right-sidebar-unit-tests
 (fn [db _]
   (get-in db [:pipeline-editor :right-sidebar-unit-tests])))

(defn- sandbox-status->ui [status]
  (case status
    "SUCCESS" "completed"
    "FAILURE" "failed"
    "QUEUED"  "pending"
    "BLOCKED" "blocked"
    nil))

(defn- index-by
  "`{(f x) x}`, keeping the first match as `find-by-field` did."
  [f coll]
  (reduce (fn [m x]
            (let [k (f x)]
              (if (or (nil? k) (contains? m k)) m (assoc m k x))))
          {} coll))

(defn build-lookups
  "Id-keyed lookups for `enrich-node`, built once per recompute."
  [{:keys [groups sources registry-models statuses model-statuses sandbox-executions]}]
  {:groups             (index-by :pipeline-group/id groups)
   :sources            (index-by :source-id sources)
   :registry-models    (index-by :analytics-model-registry/project-name registry-models)
   :statuses           (index-by :transform-id statuses)
   :model-statuses     (index-by :model-id model-statuses)
   :sandbox-executions (index-by :sandbox-transform-execution/master-id sandbox-executions)})

(defn resolve-subtitle [{:keys [type group-id metadata]} lookups]
  (case type
    "transform" (or (get-in lookups [:groups group-id :pipeline-group/name])
                    group-id)
    "extract"   (let [source (get-in lookups [:sources group-id])]
                  ;; :source-name comes with the sandbox DAG payload; the live DAG
                  ;; only carries source-id, so fall back to the sources-list lookup.
                  (or (:source-name metadata) (:name source) (:source-type source) group-id))
    "table"     group-id
    ;; Label a model node with its registry model's human display-name,
    ;; resolved from the loaded registry by `model-type` (the registry_model /
    ;; project-name); fall back to the raw model-type, then the group id.
    "model" (or (get-in lookups [:registry-models (:model-type metadata)
                                 :analytics-model-registry/display-name])
                (:model-type metadata)
                group-id)
    group-id))

(defn container-label
  "Resolve a container box's heading. Group/source boxes reuse `resolve-subtitle`
   (transform group name / extract source name); model-group boxes resolve the
   registry model's display name, keyed by its project name (`group-id`)."
  [node lookups registry-names]
  ;; `:kind` is a string ("transform-group"/"source"/"model-group") — it loses its
  ;; keyword-ness on the clj->js → ELK → js->clj round trip in `minorodata.pipeline-dag.layout/do-layout`.
  (let [{:keys [kind group-id]} (:data node)]
    (assoc-in node [:data :label]
              (if (= kind "model-group")
                (get registry-names group-id group-id)
                (let [type (case kind "transform-group" "transform" "source" "extract" nil)]
                  (resolve-subtitle {:type type :group-id group-id} lookups))))))

(defn enrich-node [{:keys [node-id preview-id sandbox? lookups]} node]
  (let [{:keys [id data]} node
        type         (:type data)
        transform?   (= "transform" type)
        model?       (= "model" type)
        group-id     (:group-id data)
        transform-id (get-in data [:metadata :transform-id])
        model-id     (get-in data [:metadata :model-id])
        live-status  (when transform-id
                       (get-in lookups [:statuses transform-id]))
        model-status (when model-id
                       (get-in lookups [:model-statuses model-id]))
        execution    (when transform?
                       (get-in lookups [:sandbox-executions id]))
        subtitle     (resolve-subtitle {:type type :group-id group-id :metadata (:metadata data)} lookups)]
    (cond-> node
      true
      (assoc-in [:data :subtitle] subtitle)

      (= node-id id)
      (assoc-in [:data :opened] true)

      (= (or preview-id node-id) id)
      (assoc-in [:data :selected] true)

      transform?
      (assoc-in [:data :status]
                (if sandbox?
                  (sandbox-status->ui (:sandbox-transform-execution/status execution))
                  (:status live-status)))

      (and transform? (not sandbox?))
      (update :data assoc :last-run live-status)

      (and transform? sandbox?)
      (assoc-in [:data :error-message]
                (:sandbox-transform-execution/error execution))

      ;; Model run status reuses the transform node-data keys (:status + :last-run)
      ;; so the node renders it with the same status bar. Keyed by per-version
      ;; model-id from metadata; live only — models never execute in the sandbox.
      (and model? (not sandbox?))
      (assoc-in [:data :status] (:status model-status))

      (and model? (not sandbox?))
      (assoc-in [:data :last-run] model-status))))

(re-frame/reg-sub
 :pipeline-editor.subs/dag
 (fn [[_ node-id _ sandbox?]]
   [(re-frame/subscribe [:pipeline-dag/pull [:pipeline-dag/id (get-dag-id node-id sandbox?)]])
    (re-frame/subscribe [:pipeline-group/pull-many])
    (re-frame/subscribe [:transform-status-list/prop :transform-status-list/statuses])
    (re-frame/subscribe [:app-route/prop :app-route/query])
    (re-frame/subscribe [:pipeline-editor.subs/sources])
    (re-frame/subscribe [:sandbox-transform-execution/pull-many])
    (re-frame/subscribe [:analytics-model-registry/pull-many])
    (re-frame/subscribe [:statuses.subs/all :model])])
 (fn [[dag groups statuses query sources sandbox-executions registry-models model-statuses] [_ node-id _ sandbox?]]
   (when (not-empty dag)
     ;; Filtering (direction/focus/table-bypass) and ELK layout happen upstream in
     ;; :sandbox.events/relayout-dag whenever the filters change, and land in
     ;; :view-nodes/:view-edges. Here we only join in the live status/subtitle data.
     (let [lookups (build-lookups {:groups             groups
                                   :sources            sources
                                   :registry-models    registry-models
                                   :statuses           statuses
                                   :model-statuses     model-statuses
                                   :sandbox-executions sandbox-executions})
           ctx {:node-id    node-id
                :preview-id (:tab-preview-id query)
                :sandbox?   sandbox?
                :lookups    lookups}
           ;; project name -> display name, for labelling model-group boxes when
           ;; the `:groups?` filter is on (see `container-label`).
           registry-names (into {}
                                (map (juxt :analytics-model-registry/project-name
                                           :analytics-model-registry/display-name))
                                registry-models)]
       {:id      (get-dag-id node-id sandbox?)
        :nodes   (mapv (fn [node]
                         (if (container? node)
                           (container-label node lookups registry-names)
                           (enrich-node ctx node)))
                       (:pipeline-dag/view-nodes dag))
        :edges   (:pipeline-dag/view-edges dag)
        :filters (:pipeline-dag/filters dag)}))))


(re-frame/reg-sub
 :pipeline-editor.subs/transform-with-output-table
 (fn []
   [(re-frame/subscribe [:pipeline-transform/pull-many])])
 (fn [[transforms] [_ id]]
   (find-first #((set (:pipeline-transform/output-tables %)) id) transforms)))

(re-frame/reg-sub
 :pipeline-editor.subs/live-preview?
 (fn [db]
   (get-in db [:pipeline-editor :live-preview])))

(re-frame/reg-sub
 :pipeline-editor.subs/features
 :<- [:unleash/get-variant "fe.pipeline-editor"]
 (fn [variant]
   (js->clj (try-parse-features variant) :keywordize-keys true)))

(re-frame/reg-sub
 :pipeline-editor.subs/enabled-feature
 :<- [:pipeline-editor.subs/features]
 (fn [features [_ feature]]
   (get features (keyword feature) false)))

(re-frame/reg-sub
 :sandbox.subs/enabled?
 :<- [:pipeline-editor.subs/live-preview?]
 (fn [live-preview?]
   (not live-preview?)))


(re-frame/reg-sub
 :pipeline-editor.subs/tab-loading?
 :<- [:mold-http/loading? :connections.api/fetch-transform-statuses-request]
 :<- [:mold-http/loading? :transform.api/get-latest-version-by-master-id]
 :<- [:mold-http/loading? :sandbox.api/fetch-sandbox-transforms]
 :<- [:mold-http/loading? :dag.api/load-dag-metadata]
 (fn [[statuses transform sandbox-transform dag]]
   (boolean (or statuses transform sandbox-transform dag))))

(re-frame/reg-sub
 :sandbox.subs/versions
 :<- [:sandbox-version/pull-many '[:* {:sandbox-version/created-by [:searchable-user/id :searchable-user/username]}]]
 (fn [versions]
   (sort-by #(-> % :sandbox-version/created-at parseISO) > versions)))

(re-frame/reg-sub
 :sandbox.subs/tables-tree
 :<- [:sandbox-table/pull-many]
 (fn [tables]
   (->> tables
        (group-by :sandbox-table/catalog)
        (mapv (fn [[catalog tables]]
                [catalog (->> tables
                              (mapv (fn [{:sandbox-table/keys [catalog id schema table]}]
                                      {:id     id
                                       :table  table
                                       :schema schema
                                       :catalog catalog}))
                              (sort-by :table)
                              (group-by :schema)
                              (sort-by first))]))
        (sort-by first))))

(re-frame/reg-sub
 :sandbox.subs/search-results
 :<- [:sandbox-transform/pull-many]
 :<- [:sandbox-table/pull-many]
 :<- [:pipeline-group/pull-many]
 (fn [[transforms tables groups] [_ {:keys [query specificity]}]]
   (let [query (some-> query str/lower-case)]
     {:transforms (when (and (not (str/blank? query)) (#{"all" "transforms"} specificity))
                    (->> transforms
                         (sort-by :sandbox-transform/name)
                         (filter (fn [t]
                                   (or (str/includes? (str/lower-case (:sandbox-transform/name t)) query)
                                       (str/includes? (str/lower-case (or (:sandbox-transform/sql t)
                                                                          (:sandbox-transform/command t)
                                                                          ""))
                                                      query))))
                         (take 5)
                         (mapv (comp
                                #(assoc % :group (drop-ns-in-keys (find-by-field groups :pipeline-group/id (:group-id %))))
                                drop-ns-in-keys))))
      :tables (when (and (not (str/blank? query)) (#{"all" "tables"} specificity))
                (->> tables
                     (sort-by :sandbox-table/id)
                     (filter (fn [t]
                               (and (not (str/blank? query))
                                    (or (str/includes? (str/lower-case (:sandbox-table/id t)) query)
                                        (some #(str/includes? (str/lower-case (or (:column_name %) (:column %))) query)
                                              (:sandbox-table/columns t))))))
                     (take 5)
                     (mapv drop-ns-in-keys)))})))

(re-frame/reg-sub
 :sandbox.subs/can-commit?
 :<- [:users.subs/has-permissions? [:pipeline-editor/write]]
 (fn [has-permission]
   has-permission))

(re-frame/reg-sub
 :sandbox.subs/commits
 :<- [:commit/pull-many '[* {:commit/created-by [:searchable-user/username]}]]
 (fn [commits]
   (into [] (sort-by :commit/order commits))))

(defn- commit-matches-search? [commit query]
  (let [q (str/lower-case query)
        committer (some-> (get-in commit [:commit/created-by :searchable-user/username])
                          str/lower-case)
        message (some-> (:commit/comment commit) str/lower-case)
        transform-names (map #(some-> (get-in % [:transform :name]) str/lower-case)
                             (:commit/revision commit))]
    (or (and committer (str/includes? committer q))
        (and message (str/includes? message q))
        (some #(and % (str/includes? % q)) transform-names))))

(defn- commit-in-date-range? [commit [from to]]
  (when (and from to)
    (let [created-ms (some-> (:commit/created-at commit) js/Date. .getTime)
          from-ms    (.getTime from)
          to-ms      (.getTime to)]
      (and created-ms (>= created-ms from-ms) (<= created-ms to-ms)))))

(re-frame/reg-sub
 :sandbox.subs/filtered-commits
 :<- [:sandbox.subs/commits]
 (fn [commits [_ {:keys [query date-range]}]]
   (cond->> commits
     (not (str/blank? query))
     (filterv #(commit-matches-search? % query))
     (= 2 (count date-range))
     (filterv #(commit-in-date-range? % date-range)))))

(re-frame/reg-sub
 :pipeline-editor.subs/sandbox-metadata
 (fn [db]
   (get-in db [:pipeline-editor :sandbox-metadata])))
